
import React, { useState, useMemo } from 'react';
import ReactApexChart from 'react-apexcharts';

// ...existing code...

function getAgregacoesDisponiveis(dados) {
  if (!dados || dados.length < 2) return { ano: false, mes: false, semana: false };
  const datas = dados.map(d => new Date(d.data)).sort((a, b) => a - b);
  const primeira = datas[0];
  const ultima = datas[datas.length - 1];
  // Ano completo: pelo menos um ano de 1/jan a 31/dez
  let temAno = false;
  for (let ano = primeira.getFullYear(); ano <= ultima.getFullYear(); ano++) {
    const inicio = new Date(ano, 0, 1);
    const fim = new Date(ano, 11, 31);
    if (datas[0] <= inicio && datas[datas.length-1] >= fim) {
      temAno = true;
      break;
    }
  }
  // Mês completo: pelo menos um mês de 1 ao último dia
  let temMes = false;
  for (let ano = primeira.getFullYear(); ano <= ultima.getFullYear(); ano++) {
    for (let mes = 0; mes < 12; mes++) {
      const inicio = new Date(ano, mes, 1);
      const fim = new Date(ano, mes + 1, 0);
      if (datas[0] <= inicio && datas[datas.length-1] >= fim) {
        temMes = true;
        break;
      }
    }
    if (temMes) break;
  }
  // Semana completa: pelo menos uma semana de segunda a domingo
  let temSemana = false;
  let d = new Date(primeira);
  d.setDate(d.getDate() + (1 - d.getDay() + 7) % 7); // próxima segunda
  while (d <= ultima) {
    const inicio = new Date(d);
    const fim = new Date(d); fim.setDate(fim.getDate() + 6);
    if (datas[0] <= inicio && datas[datas.length-1] >= fim) {
      temSemana = true;
      break;
    }
    d.setDate(d.getDate() + 7);
  }
  return {
    ano: temAno,
    mes: temMes,
    semana: temSemana
  };
}


function formatDate(date, mode) {
  const d = new Date(date);
  if (mode === 'ano') return d.getFullYear().toString();
  if (mode === 'mes') return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  if (mode === 'semana') {
    const firstDay = new Date(d.getFullYear(), 0, 1);
    const days = Math.floor((d - firstDay) / (24 * 60 * 60 * 1000));
    const week = Math.ceil((days + firstDay.getDay() + 1) / 7);
    return `${d.getFullYear()}-S${String(week).padStart(2, '0')}`;
  }
  // Dia
  return d.toLocaleDateString('pt-BR');
}

function aggregateData(dados, modo) {
  if (modo === 'dia') return dados;
  const agrupado = {};
  dados.forEach(d => {
    const chave = formatDate(d.data, modo);
    if (!agrupado[chave]) {
      agrupado[chave] = { precipitacao: 0, et0: 0, count: 0, data: chave, datas: [] };
    }
    agrupado[chave].precipitacao += d.precipitacao;
    agrupado[chave].et0 += d.et0;
    agrupado[chave].count++;
    agrupado[chave].datas.push(d.data);
  });
  // Só inclui períodos completos
  return Object.values(agrupado)
    .filter(d => {
      if (modo === 'ano') {
        const ano = parseInt(d.data);
        return d.datas.some(date => new Date(date).getMonth() === 0 && new Date(date).getDate() === 1) &&
               d.datas.some(date => new Date(date).getMonth() === 11 && new Date(date).getDate() === 31);
      }
      if (modo === 'mes') {
        const [ano, mes] = d.data.split('-');
        const ultimoDia = new Date(parseInt(ano), parseInt(mes), 0).getDate();
        return d.datas.some(date => new Date(date).getDate() === 1) &&
               d.datas.some(date => new Date(date).getDate() === ultimoDia);
      }
      if (modo === 'semana') {
        // Considera semana completa se tem 7 dias
        return d.datas.length === 7;
      }
      return true;
    })
    .map(d => ({
      dataFormatada: d.data,
      precipitacao: d.precipitacao,
      et0: d.et0
    }));
}

export default function GraficoHistoricoApex({ dados }) {
  const [modo, setModo] = useState('dia'); // dia, semana, mes, ano
  const agregacoesDisponiveis = useMemo(() => getAgregacoesDisponiveis(dados), [dados]);

  // Garante que o modo selecionado sempre seja válido
  React.useEffect(() => {
    if (!agregacoesDisponiveis[modo] && modo !== 'dia') setModo('dia');
  }, [agregacoesDisponiveis, modo]);

  // Downsampling só para modo dia
  let dadosPlot = useMemo(() => {
    if (modo !== 'dia') return aggregateData(dados, modo);
    if (dados.length > 365) {
      const step = Math.ceil(dados.length / 365);
      return dados.filter((_, idx) => idx % step === 0);
    }
    return dados;
  }, [dados, modo]);

  const avisoDownsampling = modo === 'dia' && dados.length > 365
    ? `Exibindo 1 ponto a cada ${Math.ceil(dados.length / 365)} dias para melhor visualização. Use o zoom para detalhes.`
    : null;

  const temValores = dadosPlot.some(d => d.precipitacao > 0 || d.et0 > 0);

  const series = [
    {
      name: 'Precipitação',
      data: dadosPlot.map(d => ({ x: d.dataFormatada, y: d.precipitacao }))
    },
    {
      name: 'ET0',
      data: dadosPlot.map(d => ({ x: d.dataFormatada, y: d.et0 }))
    }
  ];

  const options = {
    chart: {
      type: 'line',
      zoom: { enabled: true },
      toolbar: { show: true }
    },
    stroke: { width: 3, curve: 'smooth' },
    xaxis: {
      type: 'category',
      title: { text: 'Data' },
      labels: {
        rotate: -45,
        formatter: function(value) {
          if (modo === 'dia' && typeof value === 'string' && value.length >= 5) {
            return value.slice(0, 5);
          }
          return value;
        },
        show: true,
        style: { fontSize: '11px' },
        hideOverlappingLabels: true
      },
      tickAmount: Math.min(8, dadosPlot.length),
      axisBorder: { show: true },
      axisTicks: { show: true }
    },
    yaxis: {
      title: { text: 'mm' },
      min: 0,
      forceNiceScale: true,
      labels: {
        formatter: function (val) {
          return val.toFixed(1);
        }
      }
    },
    tooltip: {
      shared: true,
      intersect: false,
      x: { format: 'dd/MM' },
      y: {
        formatter: (val) => `${val.toFixed(1)} mm`
      }
    },
    legend: {
      position: 'top',
      horizontalAlign: 'center',
      fontSize: '14px'
    },
    colors: ['#2196f3', '#ff9800'],
    grid: { borderColor: '#eee' }
  };

  return (
    <div style={{ width: '100%', minHeight: 340 }}>
      <div style={{ marginBottom: 8, display: 'flex', alignItems: 'center', gap: 10 }}>
        <span style={{ fontWeight: 600, color: '#1976d2' }}>Visualizar por:</span>
        <select value={modo} onChange={e => setModo(e.target.value)} style={{ padding: 4, borderRadius: 4 }}>
          <option value="dia">Dia</option>
          {agregacoesDisponiveis.semana && <option value="semana">Semana</option>}
          {agregacoesDisponiveis.mes && <option value="mes">Mês</option>}
          {agregacoesDisponiveis.ano && <option value="ano">Ano</option>}
        </select>
      </div>
      {avisoDownsampling && (
        <div style={{ color: '#1976d2', fontWeight: 600, marginBottom: 8 }}>{avisoDownsampling}</div>
      )}
      {!temValores && (
        <div style={{ color: '#d32f2f', fontWeight: 600, marginBottom: 12 }}>
          Nenhum dado válido de precipitação ou ETo para o período selecionado.
        </div>
      )}
      <ReactApexChart options={options} series={series} type="line" height={320} />
    </div>
  );
}
